import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, UserCheck, Database, Globe, Mail, Phone } from 'lucide-react';
import Footer from '../../components/footer';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

const Section = ({  title, children, isDarkMode }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-4">

      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    </div>
    <div className={`leading-relaxed space-y-3 pl-[52px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
      {children}
    </div>
  </div>
);

const KebijakanPrivasi = () => {
  const { isDarkMode } = useDarkMode();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-28 pb-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Kebijakan Privasi</h1>
          <p className="text-gray-300 text-lg">PT Telekomunikasi Internet Indonesia (Telnet Indonesia)</p>
          <p className="text-gray-400 text-sm mt-2">Terakhir diperbarui: 1 Februari 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 py-12">
        <div className={`rounded-2xl shadow-sm border p-6 sm:p-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
          
          {/* Pendahuluan */}
          <div className={`mb-10 p-6 rounded-xl border ${isDarkMode ? 'bg-green-900/20 border-green-800/40' : 'bg-green-50 border-green-100'}`}>
            <p className={`leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              PT Telekomunikasi Internet Indonesia ("Telnet Indonesia", "Kami") berkomitmen untuk melindungi privasi dan keamanan data pribadi Anda. Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan, menggunakan, menyimpan, dan melindungi informasi pribadi Anda saat menggunakan layanan kami, termasuk layanan internet broadband, dedicated, CCTV, dan solusi IT lainnya. Kebijakan ini berlaku untuk seluruh layanan yang disediakan oleh Telnet Indonesia, baik melalui website, aplikasi, maupun layanan langsung.
            </p>
          </div>

          <Section  title="1. Data yang Kami Kumpulkan" isDarkMode={isDarkMode}>
            <p><strong>a. Data Pribadi:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Nama lengkap, alamat email, nomor telepon</li>
              <li>Alamat tempat tinggal/instalasi</li>
              <li>Nomor KTP/identitas resmi</li>
              <li>Informasi pembayaran (nomor rekening, kartu kredit)</li>
              <li>Data lokasi untuk keperluan instalasi dan coverage check</li>
            </ul>
            <p className="mt-3"><strong>b. Data Teknis:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Alamat IP, MAC Address perangkat</li>
              <li>Log penggunaan bandwidth dan aktivitas jaringan</li>
              <li>Informasi perangkat (router, ONT, CCTV)</li>
              <li>Data performa jaringan dan speed test</li>
            </ul>
            <p className="mt-3"><strong>c. Cookies & Data Otomatis:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Cookies untuk preferensi pengguna dan sesi login</li>
              <li>Data analitik website (halaman yang dikunjungi, durasi kunjungan)</li>
              <li>Informasi browser dan sistem operasi</li>
            </ul>
          </Section>

          <Section  title="2. Tujuan Pengumpulan Data" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Penyediaan Layanan:</strong> Memproses pendaftaran, instalasi, aktivasi, dan pemeliharaan layanan internet dan IT</li>
              <li><strong>Penagihan & Pembayaran:</strong> Mengelola tagihan bulanan, pembayaran, dan riwayat transaksi</li>
              <li><strong>Dukungan Pelanggan:</strong> Menangani keluhan, permintaan bantuan teknis, dan komunikasi terkait layanan</li>
              <li><strong>Peningkatan Layanan:</strong> Menganalisis penggunaan untuk meningkatkan kualitas jaringan dan layanan</li>
              <li><strong>Keamanan:</strong> Mendeteksi dan mencegah penyalahgunaan jaringan, fraud, dan ancaman keamanan</li>
              <li><strong>Kepatuhan Hukum:</strong> Memenuhi kewajiban regulasi dari Kominfo, BRTI, dan lembaga terkait</li>
              <li><strong>Komunikasi:</strong> Mengirimkan informasi terkait layanan, promo, dan pembaruan penting</li>
            </ul>
          </Section>

          <Section  title="3. Penyimpanan & Keamanan Data" isDarkMode={isDarkMode}>
            <p>Kami menerapkan standar keamanan tinggi untuk melindungi data Anda:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Enkripsi:</strong> Seluruh data sensitif dienkripsi menggunakan standar AES-256 saat disimpan dan SSL/TLS saat ditransmisikan</li>
              <li><strong>Akses Terbatas:</strong> Hanya karyawan yang berwenang yang memiliki akses ke data pribadi, dengan sistem otorisasi berlapis</li>
              <li><strong>Infrastruktur Aman:</strong> Data disimpan di data center Tier III dengan standar keamanan fisik dan digital yang ketat</li>
              <li><strong>Backup Berkala:</strong> Backup data dilakukan secara berkala dengan penyimpanan terenkripsi di lokasi terpisah</li>
              <li><strong>Monitoring 24/7:</strong> Sistem keamanan jaringan dimonitor sepanjang waktu untuk mendeteksi ancaman</li>
              <li><strong>Retensi Data:</strong> Data pribadi disimpan selama masa berlangganan aktif dan hingga 5 tahun setelah pemutusan layanan, sesuai ketentuan perundang-undangan yang berlaku</li>
            </ul>
          </Section>

          <Section  title="4. Hak Pengguna" isDarkMode={isDarkMode}>
            <p>Sebagai pelanggan Telnet Indonesia, Anda memiliki hak-hak berikut:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Hak Akses:</strong> Meminta salinan data pribadi yang kami simpan tentang Anda</li>
              <li><strong>Hak Koreksi:</strong> Meminta perbaikan atas data yang tidak akurat atau tidak lengkap</li>
              <li><strong>Hak Penghapusan:</strong> Meminta penghapusan data pribadi Anda, dengan ketentuan bahwa data yang diperlukan untuk kepatuhan hukum akan tetap disimpan</li>
              <li><strong>Hak Pembatasan:</strong> Membatasi cara kami memproses data Anda</li>
              <li><strong>Hak Portabilitas:</strong> Meminta data Anda dalam format yang dapat dibaca mesin</li>
              <li><strong>Hak Keberatan:</strong> Menolak pemrosesan data untuk keperluan pemasaran langsung</li>
            </ul>
            <p className="mt-3">Untuk menggunakan hak-hak ini, hubungi Data Protection Officer kami melalui kontak yang tersedia di bawah.</p>
          </Section>

          <Section  title="5. Pembagian Data kepada Pihak Ketiga" isDarkMode={isDarkMode}>
            <p>Kami <strong>tidak menjual</strong> data pribadi Anda. Data hanya dibagikan kepada:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Mitra Teknis:</strong> Vendor perangkat jaringan dan IT untuk keperluan instalasi dan maintenance (Mikrotik, Ubiquiti, Huawei, dll.)</li>
              <li><strong>Penyedia Pembayaran:</strong> Bank dan payment gateway untuk pemrosesan transaksi pembayaran</li>
              <li><strong>Otoritas Berwenang:</strong> Lembaga pemerintah atau penegak hukum jika diwajibkan oleh undang-undang atau perintah pengadilan</li>
              <li><strong>Upstream Provider:</strong> Penyedia jaringan upstream untuk alokasi IP dan peering (dengan data minimal yang diperlukan)</li>
            </ul>
            <p className="mt-3">Seluruh pihak ketiga diwajibkan menandatangani perjanjian kerahasiaan (NDA) dan mematuhi standar keamanan data kami.</p>
          </Section>

          <Section  title="6. Perubahan Kebijakan" isDarkMode={isDarkMode}>
            <p>Kami berhak memperbarui Kebijakan Privasi ini sewaktu-waktu. Perubahan material akan diberitahukan melalui:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Pemberitahuan di website resmi</li>
              <li>Email kepada pelanggan terdaftar</li>
              <li>Notifikasi melalui dashboard pelanggan</li>
            </ul>
            <p className="mt-3">Penggunaan layanan secara berkelanjutan setelah perubahan dianggap sebagai persetujuan terhadap kebijakan yang diperbarui.</p>
          </Section>

          <Section title="7. Kontak Data Protection Officer" isDarkMode={isDarkMode}>
            <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Data Protection Officer (DPO)</p>
              <p className={`mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>PT Telekomunikasi Internet Indonesia</p>
              <div className="space-y-2 mt-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-600" />
                  <span>dpo@telnet.co.id</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-600" />
                  <span>08777 888 2022</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-600" />
                  <span>Jl. Dr. Mr. Mohd. Hasan No. 33, Banda Aceh, 23245</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Footer note */}
          <div className={`mt-10 pt-6 border-t text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Kebijakan Privasi ini tunduk pada hukum Republik Indonesia, termasuk Undang-Undang No. 27 Tahun 2022 tentang Pelindungan Data Pribadi (UU PDP).
            </p>
            <div className="flex justify-center gap-4 mt-4 text-sm">
              <Link to="/ketentuan-layanan" className="text-green-600 hover:underline">Ketentuan Layanan</Link>
              <Link to="/alur-transaksi" className="text-green-600 hover:underline">Alur Transaksi</Link>
              <Link to="/faq" className="text-green-600 hover:underline">FAQ</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default KebijakanPrivasi;
